import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Eye, EyeOff, Upload, Clock, Type, Image as ImageIcon, AlignLeft, Rows, Save, Trash2, Plus, X, Rss, CloudSun, Sparkles, Trophy, Vote } from "lucide-react";

const CW = 1920, CH = 1080;
const DEFAULT_LEAGUES = ["epl", "ucl", "laliga", "afcon"];
const TYPES = [
  { v: "text", label: "Text", icon: Type },
  { v: "lower_third", label: "Lower third", icon: AlignLeft },
  { v: "ticker", label: "Ticker / crawl", icon: Rows },
  { v: "sports", label: "Sports ticker", icon: Trophy },
  { v: "election", label: "Election map", icon: Vote },
  { v: "logo", label: "Logo / image", icon: ImageIcon },
  { v: "image", label: "Image", icon: ImageIcon },
  { v: "clock", label: "Clock", icon: Clock },
  { v: "weather", label: "Weather", icon: CloudSun },
];
const CORNERS = [
  { k: "top-left", label: "TL", x: 60, y: 50 },
  { k: "top-right", label: "TR", x: (w) => CW - w - 60, y: 50 },
  { k: "bottom-left", label: "BL", x: 60, y: (h) => CH - h - 50 },
  { k: "bottom-right", label: "BR", x: (w) => CW - w - 60, y: (h) => CH - h - 50 },
];
const isImgType = (t) => t === "logo" || t === "image" || t === "apng";
const hasTextFeed = (t) => t === "ticker" || t === "text" || t === "lower_third";
const DOW = [["S", 0], ["M", 1], ["T", 2], ["W", 3], ["T", 4], ["F", 5], ["S", 6]];

// Mini on-air preview rendered from a preset's stored layer snapshot.
function PresetThumb({ layers }) {
  const vis = (layers || []).filter((l) => l.visible).sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
  return (
    <div className="relative w-full aspect-video rounded-sm overflow-hidden border border-zinc-700 bg-[repeating-conic-gradient(#141414_0%_25%,#0c0c0c_0%_50%)] bg-[length:14px_14px]" data-testid="gfx-preset-thumb">
      {vis.map((l, i) => {
        const p = l.props || {};
        const box = {
          position: "absolute",
          left: `${(l.x / CW) * 100}%`, top: `${(l.y / CH) * 100}%`,
          width: `${(l.width / CW) * 100}%`, height: `${(l.height / CH) * 100}%`,
          opacity: l.opacity != null ? l.opacity : 1,
          overflow: "hidden", display: "flex", alignItems: "center",
        };
        if (isImgType(l.type) && p.image_url) {
          return <img key={i} src={p.image_url} alt="" style={{ ...box, objectFit: p.fit || "contain" }} />;
        }
        const txt = l.type === "clock" ? "12:00" : (p.text || (l.type === "weather" ? "Weather" : l.type === "sports" ? "Sports" : l.name) || "");
        const hasBg = p.background || l.type === "lower_third" || l.type === "ticker" || l.type === "sports";
        return (
          <div key={i} style={box}>
            <div style={{
              width: "100%", color: p.color || "#fff",
              background: p.background || (hasBg ? "rgba(0,0,0,0.55)" : "transparent"),
              fontSize: `${Math.max(4, (l.height / CH) * 100 * 0.9)}px`, fontWeight: 700,
              padding: hasBg ? "0 2px" : 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{txt}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function GraphicsEditorDialog({ open, onClose, channel }) {
  const [layers, setLayers] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nowStr, setNowStr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [applyIds, setApplyIds] = useState([]);
  const [leagueCatalog, setLeagueCatalog] = useState([]);
  const [sportsText, setSportsText] = useState({});
  const [electionOpts, setElectionOpts] = useState([]);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const t = () => setNowStr(new Date().toLocaleTimeString([], { hour12: false }));
    t(); const id = setInterval(t, 1000); return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    if (!channel?.id) return;
    setLoading(true);
    try {
      let r = await api.get(`/graphics?channel_id=${channel.id}`);
      let list = Array.isArray(r.data) ? r.data : [];
      if (!list.length) {
        const s = await api.post(`/graphics/seed?channel_id=${channel.id}`);
        list = Array.isArray(s.data) ? s.data : [];
      }
      list.sort((a, b) => (a.layer_index || 0) - (b.layer_index || 0));
      setLayers(list);
      setSelId((prev) => prev || list[0]?.id || null);
    } catch (e) {
      toast.error("Failed to load graphics layers");
    } finally { setLoading(false); }
  }, [channel?.id]);

  useEffect(() => { if (open) { load(); loadPresets(); } }, [open, load]);

  const loadPresets = useCallback(async () => {
    try {
      const [pr, ch] = await Promise.all([
        api.get(`/graphics-presets`),
        api.get(`/channels`),
      ]);
      setPresets(Array.isArray(pr.data) ? pr.data : []);
      setChannels(Array.isArray(ch.data) ? ch.data : []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const sel = layers.find((l) => l.id === selId) || null;

  const loadSports = useCallback(async (layer) => {
    if (!layer || layer.type !== "sports") return;
    try {
      const r = await api.get(`/graphics/${layer.id}/sports`);
      setSportsText((s) => ({ ...s, [layer.id]: r.data }));
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get(`/graphics/sports/leagues`)
      .then((r) => setLeagueCatalog(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    if (channel?.id) {
      api.get(`/elections?channel_id=${channel.id}`)
        .then((r) => setElectionOpts(Array.isArray(r.data) ? r.data : []))
        .catch(() => setElectionOpts([]));
    }
  }, [open, channel?.id]);

  useEffect(() => {
    if (!open || !sel || sel.type !== "sports") return;
    loadSports(sel);
    const id = setInterval(() => loadSports(sel), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sel?.id, sel?.type, JSON.stringify(sel?.props?.leagues)]);

  const setLocal = (id, patch) =>
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const persist = async (id, patch) => {
    try { await api.patch(`/graphics/${id}`, patch); }
    catch (e) { toast.error("Save failed"); }
  };

  const update = (id, patch) => { setLocal(id, patch); persist(id, patch); };

  const updateProp = (id, key, val) => {
    const l = layers.find((x) => x.id === id);
    const props = { ...(l?.props || {}), [key]: val };
    setLocal(id, { props });
    persist(id, { props });
  };

  const toggle = async (l) => {
    setLocal(l.id, { visible: !l.visible });
    try { await api.post(`/graphics/${l.id}/toggle`); }
    catch (e) { setLocal(l.id, { visible: l.visible }); toast.error("Toggle failed"); }
  };

  // --- Scheduling (recurring + one-off) ---
  const schedOf = (l) => l?.props?.sched || { on: false, windows: [], oneoff: null };
  const patchSched = (patch) => {
    if (!sel) return;
    const cur = schedOf(sel);
    updateProp(sel.id, "sched", { ...cur, ...patch });
  };
  const addWindow = () => {
    const sc = schedOf(sel);
    patchSched({ on: true, windows: [...(sc.windows || []), { s: "18:00", e: "22:00", days: [] }] });
  };
  const setWindow = (i, patch) => {
    const sc = schedOf(sel);
    const windows = (sc.windows || []).map((w, idx) => (idx === i ? { ...w, ...patch } : w));
    patchSched({ windows });
  };
  const removeWindow = (i) => {
    const sc = schedOf(sel);
    patchSched({ windows: (sc.windows || []).filter((_, idx) => idx !== i) });
  };
  const toggleDay = (i, dow) => {
    const sc = schedOf(sel);
    const w = (sc.windows || [])[i]; if (!w) return;
    const days = w.days || [];
    setWindow(i, { days: days.includes(dow) ? days.filter((d) => d !== dow) : [...days, dow] });
  };

  // --- Presets ---
  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name the preset first"); return; }
    setSavingPreset(true);
    try {
      await api.post(`/graphics-presets`, { channel_id: channel.id, name: presetName.trim() });
      setPresetName("");
      toast.success("Preset saved");
      loadPresets();
    } catch (e) { toast.error("Save preset failed"); }
    finally { setSavingPreset(false); }
  };
  const openApply = (p) => { setApplyingPreset(p); setApplyIds([channel.id]); };
  const runApply = async () => {
    if (!applyingPreset || !applyIds.length) return;
    try {
      const r = await api.post(`/graphics-presets/${applyingPreset.id}/apply`, { channel_ids: applyIds });
      toast.success(`Applied to ${r.data?.applied ?? applyIds.length} channel(s)`);
      if (applyIds.includes(channel.id)) { setLayers([]); setSelId(null); await load(); }
      setApplyingPreset(null);
    } catch (e) { toast.error("Apply failed"); }
  };
  const deletePreset = async (p) => {
    try { await api.delete(`/graphics-presets/${p.id}`); loadPresets(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const applyCorner = (c) => {
    if (!sel) return;
    const x = typeof c.x === "function" ? c.x(sel.width) : c.x;
    const y = typeof c.y === "function" ? c.y(sel.height) : c.y;
    update(sel.id, { x: Math.round(x), y: Math.round(y) });
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !sel) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/graphics/${sel.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLocal(sel.id, r.data);
      toast.success("Image uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Drag / resize on the canvas.
  const startDrag = (e, layer, mode) => {
    e.preventDefault(); e.stopPropagation();
    setSelId(layer.id);
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CW / rect.width, scaleY = CH / rect.height;
    dragRef.current = {
      mode, id: layer.id, startX: e.clientX, startY: e.clientY,
      ox: layer.x, oy: layer.y, ow: layer.width, oh: layer.height, scaleX, scaleY,
    };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag);
  };
  const onDrag = (e) => {
    const d = dragRef.current; if (!d) return;
    const dx = (e.clientX - d.startX) * d.scaleX;
    const dy = (e.clientY - d.startY) * d.scaleY;
    if (d.mode === "move") {
      const x = Math.max(0, Math.min(CW - d.ow, Math.round(d.ox + dx)));
      const y = Math.max(0, Math.min(CH - d.oh, Math.round(d.oy + dy)));
      setLocal(d.id, { x, y });
    } else {
      const w = Math.max(40, Math.min(CW - d.ox, Math.round(d.ow + dx)));
      const h = Math.max(24, Math.min(CH - d.oy, Math.round(d.oh + dy)));
      setLocal(d.id, { width: w, height: h });
    }
  };
  const endDrag = () => {
    const d = dragRef.current;
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", endDrag);
    if (d) {
      const l = layers.find((x) => x.id === d.id);
      const cur = document.activeElement; // noop
      // read latest from state via functional update
      setLayers((ls) => {
        const lay = ls.find((x) => x.id === d.id);
        if (lay) persist(d.id, d.mode === "move" ? { x: lay.x, y: lay.y } : { width: lay.width, height: lay.height });
        return ls;
      });
    }
    dragRef.current = null;
  };

  const layerText = (l) => {
    if (l.type === "clock") return nowStr;
    if (l.type === "sports") return sportsText[l.id]?.text || "Sports ticker";
    if (l.type === "election") return "Election map";
    return (l.props?.text) || (l.type === "ticker" ? "" : l.name) || "";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="graphics-editor-dialog">
        <DialogHeader>
          <DialogTitle>Overlay graphics · {channel?.name}</DialogTitle>
          <DialogDescription>Design up to 14 branded layers. Drag on the canvas to position, resize with the corner handle. Changes preview live and save automatically.</DialogDescription>
        </DialogHeader>

        {/* Presets bar */}
        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-2.5 space-y-2" data-testid="gfx-presets-bar">
          <div className="flex items-center gap-2">
            <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Preset name…"
              className="h-8 text-sm rounded-sm flex-1" data-testid="gfx-preset-name-input" />
            <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={savingPreset} onClick={savePreset} data-testid="gfx-preset-save-btn">
              {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save as preset
            </Button>
          </div>
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="gfx-preset-list">
              {presets.map((p) => (
                <div key={p.id} className="w-36 rounded-sm border border-zinc-700 overflow-hidden" data-testid={`gfx-preset-${p.id}`}>
                  <PresetThumb layers={p.layers} />
                  <div className="flex items-center gap-1 px-1.5 py-1 text-[11px] text-zinc-300">
                    <span className="truncate flex-1" title={p.name}>{p.name}</span>
                    <button className="text-cyan-400 hover:text-cyan-200" onClick={() => openApply(p)} data-testid={`gfx-preset-apply-${p.id}`}>Apply</button>
                    <button className="text-red-400/70 hover:text-red-300" onClick={() => deletePreset(p)} data-testid={`gfx-preset-del-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {applyingPreset && (
            <div className="rounded-sm border border-cyan-500/40 bg-cyan-500/5 p-2 space-y-2" data-testid="gfx-preset-apply-panel">
              <div className="text-[11px] text-cyan-200">Apply “{applyingPreset.name}” to channels (overwrites their layers):</div>
              <div className="flex flex-wrap gap-1.5">
                {channels.map((c) => {
                  const on = applyIds.includes(c.id);
                  return (
                    <button key={c.id} onClick={() => setApplyIds((s) => on ? s.filter((x) => x !== c.id) : [...s, c.id])}
                      className={`rounded-sm border px-2 py-1 text-[11px] ${on ? "border-cyan-400 bg-cyan-400/10 text-cyan-100" : "border-zinc-700 text-zinc-400"}`}
                      data-testid={`gfx-apply-ch-${c.slug || c.id}`}>{c.name}{c.id === channel.id ? " (this)" : ""}</button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm text-xs" disabled={!applyIds.length} onClick={runApply} data-testid="gfx-preset-apply-run">Apply</Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={() => setApplyingPreset(null)} data-testid="gfx-preset-apply-cancel">Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading layers…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            {/* Canvas + layer list */}
            <div className="space-y-3">
              <div
                ref={canvasRef}
                className="relative w-full aspect-video rounded-md overflow-hidden border border-zinc-700 bg-[repeating-conic-gradient(#141414_0%_25%,#0c0c0c_0%_50%)] bg-[length:28px_28px] select-none"
                data-testid="graphics-canvas"
              >
                {layers.filter((l) => l.visible).sort((a, b) => (a.z_index || 0) - (b.z_index || 0)).map((l) => {
                  const style = {
                    position: "absolute",
                    left: `${(l.x / CW) * 100}%`, top: `${(l.y / CH) * 100}%`,
                    width: `${(l.width / CW) * 100}%`, height: `${(l.height / CH) * 100}%`,
                    opacity: l.opacity != null ? l.opacity : 1,
                    outline: l.id === selId ? "2px solid #22d3ee" : "1px dashed rgba(255,255,255,0.25)",
                    cursor: "move", overflow: "hidden", display: "flex", alignItems: "center",
                  };
                  const p = l.props || {};
                  const hasBg = p.background || l.type === "lower_third" || l.type === "ticker" || l.type === "sports";
                  return (
                    <div key={l.id} style={style} onMouseDown={(e) => startDrag(e, l, "move")} data-testid={`gfx-canvas-layer-${l.layer_index}`}>
                      {isImgType(l.type) && p.image_url ? (
                        <img src={p.image_url} alt={l.name} className="w-full h-full" style={{ objectFit: p.fit || "contain", pointerEvents: "none" }} />
                      ) : (
                        <div style={{
                          width: "100%", color: p.color || "#fff",
                          background: p.background || (hasBg ? "rgba(0,0,0,0.55)" : "transparent"),
                          fontSize: `${Math.max(9, (l.height / CH) * 100 * 2.2)}px`, fontWeight: 700,
                          padding: hasBg ? "0.15em 0.4em" : 0, borderRadius: hasBg ? 4 : 0,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          pointerEvents: "none",
                        }}>{layerText(l) || <span className="opacity-40">{l.name}</span>}</div>
                      )}
                      {l.id === selId && (
                        <div onMouseDown={(e) => startDrag(e, l, "resize")}
                          className="absolute bottom-0 right-0 w-3 h-3 bg-cyan-400 cursor-nwse-resize"
                          data-testid="gfx-resize-handle" />
                      )}
                    </div>
                  );
                })}
                {!layers.some((l) => l.visible) && (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-500">No visible layers — enable one from the list →</div>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5" data-testid="gfx-layer-list">
                {layers.map((l) => (
                  <button key={l.id} onClick={() => setSelId(l.id)}
                    data-testid={`gfx-layer-chip-${l.layer_index}`}
                    className={`group flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] transition-colors ${l.id === selId ? "border-cyan-400 bg-cyan-400/10 text-cyan-200" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"} ${l.visible ? "" : "opacity-55"}`}>
                    <span onClick={(e) => { e.stopPropagation(); toggle(l); }} data-testid={`gfx-toggle-${l.layer_index}`} className="hover:text-white">
                      {l.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </span>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Inspector */}
            <div className="space-y-3">
              {sel ? (
                <div className="space-y-3" data-testid="gfx-inspector">
                  <div className="flex items-center justify-between">
                    <Input value={sel.name} onChange={(e) => setLocal(sel.id, { name: e.target.value })} onBlur={(e) => persist(sel.id, { name: e.target.value })}
                      className="h-8 text-sm rounded-sm" data-testid="gfx-name-input" />
                    <Switch checked={sel.visible} onCheckedChange={() => toggle(sel)} data-testid="gfx-visible-switch" className="ml-2" />
                  </div>

                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Type</Label>
                    <select value={sel.type} onChange={(e) => update(sel.id, { type: e.target.value })}
                      className="mt-1 w-full h-8 rounded-sm bg-zinc-900 border border-zinc-700 text-sm px-2" data-testid="gfx-type-select">
                      {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                  </div>

                  {isImgType(sel.type) ? (
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Artwork</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="w-14 h-14 shrink-0 rounded-sm border border-zinc-700 bg-black/40 flex items-center justify-center overflow-hidden">
                          {sel.props?.image_url ? <img src={sel.props.image_url} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-4 h-4 text-zinc-600" />}
                        </div>
                        <input ref={fileRef} type="file" accept="image/png,image/webp,image/jpeg,image/gif,image/svg+xml" className="hidden" onChange={onUpload} data-testid="gfx-image-file" />
                        <Button variant="outline" size="sm" className="rounded-sm text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="gfx-image-upload-btn">
                          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                          {sel.props?.image_url ? "Replace" : "Upload"}
                        </Button>
                      </div>
                    </div>
                  ) : sel.type === "weather" ? (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-[11px] uppercase tracking-wide text-zinc-500">City / location</Label>
                        <Input value={sel.props?.location || ""} onChange={(e) => setLocal(sel.id, { props: { ...(sel.props || {}), location: e.target.value } })}
                          onBlur={(e) => updateProp(sel.id, "location", e.target.value.trim())} className="mt-1 h-8 text-sm rounded-sm" data-testid="gfx-location-input" placeholder="e.g. Kinshasa" />
                      </div>
                      <div className="w-20">
                        <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Units</Label>
                        <select value={sel.props?.units || "c"} onChange={(e) => updateProp(sel.id, "units", e.target.value)}
                          className="mt-1 w-full h-8 rounded-sm bg-zinc-900 border border-zinc-700 text-sm px-1" data-testid="gfx-units-select">
                          <option value="c">°C</option>
                          <option value="f">°F</option>
                        </select>
                      </div>
                    </div>
                  ) : sel.type === "sports" ? (
                    <div className="space-y-2" data-testid="gfx-sports-panel">
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Trophy className="w-3 h-3" /> Leagues (live scores)</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {leagueCatalog.map((lg) => {
                          const cur = sel.props?.leagues ?? DEFAULT_LEAGUES;
                          const on = cur.includes(lg.key);
                          return (
                            <button key={lg.key} type="button"
                              onClick={() => {
                                const base = sel.props?.leagues ?? DEFAULT_LEAGUES;
                                const next = on ? base.filter((k) => k !== lg.key) : [...base, lg.key];
                                updateProp(sel.id, "leagues", next);
                              }}
                              className={`rounded-sm border px-2 py-1 text-[11px] ${on ? "border-cyan-400 bg-cyan-400/10 text-cyan-100" : "border-zinc-700 text-zinc-400"}`}
                              data-testid={`gfx-sports-league-${lg.key}`}>{lg.label}</button>
                          );
                        })}
                      </div>
                      <div className="rounded-sm border border-zinc-800 bg-black/30 p-2" data-testid="gfx-sports-preview">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wide text-zinc-500">Live now{sportsText[sel.id]?.live_count ? ` · ${sportsText[sel.id].live_count}` : ""}</span>
                          <button type="button" className="text-[10px] text-cyan-400 hover:text-cyan-200" onClick={() => loadSports(sel)} data-testid="gfx-sports-refresh">Refresh</button>
                        </div>
                        <p className="text-[11px] text-zinc-200 mt-1 break-words" data-testid="gfx-sports-preview-text">
                          {sportsText[sel.id]?.live_count > 0
                            ? sportsText[sel.id].text
                            : (sportsText[sel.id]?.next ? `No live matches. Next: ${sportsText[sel.id].next}` : "No live matches right now.")}
                        </p>
                      </div>
                      <p className="text-[10px] text-zinc-500">Scores refresh ~every 30s. Only live matches appear on-air.</p>
                    </div>
                  ) : sel.type === "election" ? (
                    <div className="space-y-2" data-testid="gfx-election-panel">
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Vote className="w-3 h-3" /> Election (Magic Wall)</Label>
                      <select value={sel.props?.election_id || ""} onChange={(e) => updateProp(sel.id, "election_id", e.target.value)}
                        className="w-full h-8 rounded-sm bg-zinc-900 border border-zinc-700 text-sm px-2" data-testid="gfx-election-select">
                        <option value="">— Select an election —</option>
                        {electionOpts.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.status})</option>)}
                      </select>
                      <div className="flex gap-1.5">
                        {["board", "scorebug"].map((m) => {
                          const on = (sel.props?.mode || "board") === m;
                          return (
                            <button key={m} type="button" onClick={() => updateProp(sel.id, "mode", m)}
                              className={`flex-1 rounded-sm border px-2 py-1 text-[11px] ${on ? "border-cyan-400 bg-cyan-400/10 text-cyan-100" : "border-zinc-700 text-zinc-400"}`}
                              data-testid={`gfx-election-mode-${m}`}>{m === "board" ? "Full board" : "Scorebug"}</button>
                          );
                        })}
                      </div>
                      {(sel.props?.mode) === "scorebug" && (
                        <select value={sel.props?.scorebug_target || "national"} onChange={(e) => updateProp(sel.id, "scorebug_target", e.target.value)}
                          className="w-full h-8 rounded-sm bg-zinc-900 border border-zinc-700 text-sm px-2" data-testid="gfx-election-target">
                          <option value="national">National total</option>
                          {(electionOpts.find((e) => e.id === sel.props?.election_id)?.regions || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.abbr}</option>)}
                        </select>
                      )}
                      <p className="text-[10px] text-zinc-500">Build &amp; control results on the Magic Wall page. This layer burns the live map into the feed at the box below — use the BL/BR corners for a scorebug, or full-frame.</p>
                    </div>
                  ) : sel.type !== "clock" ? (
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Text</Label>
                      <Input value={sel.props?.text || ""} onChange={(e) => setLocal(sel.id, { props: { ...(sel.props || {}), text: e.target.value } })}
                        onBlur={(e) => updateProp(sel.id, "text", e.target.value)} className="mt-1 h-8 text-sm rounded-sm" data-testid="gfx-text-input" placeholder="Overlay text" />
                    </div>
                  ) : null}

                  {!isImgType(sel.type) && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Text color</Label>
                        <input type="color" value={sel.props?.color || "#ffffff"}
                          onChange={(e) => setLocal(sel.id, { props: { ...(sel.props || {}), color: e.target.value } })}
                          onBlur={(e) => updateProp(sel.id, "color", e.target.value)}
                          className="mt-1 w-full h-8 rounded-sm bg-transparent border border-zinc-700" data-testid="gfx-color-input" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Background</Label>
                        <input type="color" value={sel.props?.background || "#000000"}
                          onChange={(e) => setLocal(sel.id, { props: { ...(sel.props || {}), background: e.target.value } })}
                          onBlur={(e) => updateProp(sel.id, "background", e.target.value)}
                          className="mt-1 w-full h-8 rounded-sm bg-transparent border border-zinc-700" data-testid="gfx-bg-input" />
                      </div>
                    </div>
                  )}

                  {(sel.type === "ticker" || sel.type === "sports") && (
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Crawl speed (px/s): {sel.props?.speed || 120}</Label>
                      <input type="range" min="40" max="300" step="10" value={sel.props?.speed || 120}
                        onChange={(e) => updateProp(sel.id, "speed", Number(e.target.value))} className="w-full" data-testid="gfx-speed-range" />
                    </div>
                  )}

                  {hasTextFeed(sel.type) && (
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Rss className="w-3 h-3" /> Live text feed (RSS / URL)</Label>
                      <Input value={sel.props?.feed_url || ""}
                        onChange={(e) => setLocal(sel.id, { props: { ...(sel.props || {}), feed_url: e.target.value } })}
                        onBlur={(e) => updateProp(sel.id, "feed_url", e.target.value.trim())}
                        className="mt-1 h-8 text-xs rounded-sm" placeholder="https://…/rss.xml (overrides text)" data-testid="gfx-feed-input" />
                      <p className="text-[10px] text-zinc-500 mt-0.5">Headlines refresh every 5 min. Leave blank to use the static text.</p>
                    </div>
                  )}

                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Opacity: {Math.round((sel.opacity ?? 1) * 100)}%</Label>
                    <input type="range" min="0" max="1" step="0.05" value={sel.opacity ?? 1}
                      onChange={(e) => setLocal(sel.id, { opacity: Number(e.target.value) })}
                      onMouseUp={(e) => persist(sel.id, { opacity: Number(e.target.value) })}
                      onTouchEnd={(e) => persist(sel.id, { opacity: Number(e.target.value) })}
                      className="w-full" data-testid="gfx-opacity-range" />
                  </div>

                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Transition</Label>
                    <select value={sel.props?.anim || "none"} onChange={(e) => updateProp(sel.id, "anim", e.target.value)}
                      className="mt-1 w-full h-8 rounded-sm bg-zinc-900 border border-zinc-700 text-sm px-2" data-testid="gfx-anim-select">
                      <option value="none">None</option>
                      <option value="fade">Fade in / out</option>
                      <option value="slide">Slide in / out</option>
                    </select>
                    <p className="text-[10px] text-zinc-500 mt-0.5">Animates on schedule edges in preview; the live stream fades in at push start.</p>
                  </div>

                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Position (1920×1080)</Label>
                    <div className="mt-1 grid grid-cols-4 gap-1.5">
                      {["x", "y", "width", "height"].map((k) => (
                        <div key={k}>
                          <span className="text-[9px] text-zinc-500 uppercase">{k === "width" ? "W" : k === "height" ? "H" : k}</span>
                          <Input type="number" value={Math.round(sel[k] || 0)}
                            onChange={(e) => setLocal(sel.id, { [k]: Number(e.target.value) })}
                            onBlur={(e) => persist(sel.id, { [k]: Number(e.target.value) })}
                            className="h-7 text-xs rounded-sm px-1" data-testid={`gfx-${k}-input`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-1">
                      {CORNERS.map((c) => (
                        <Button key={c.k} variant="outline" size="sm" className="flex-1 h-7 text-[10px] rounded-sm" onClick={() => applyCorner(c)} data-testid={`gfx-corner-${c.k}`}>{c.label}</Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">Z-index (stacking): {sel.z_index || 0}</Label>
                    <input type="range" min="0" max="13" step="1" value={sel.z_index || 0}
                      onChange={(e) => setLocal(sel.id, { z_index: Number(e.target.value) })}
                      onMouseUp={(e) => persist(sel.id, { z_index: Number(e.target.value) })}
                      className="w-full" data-testid="gfx-zindex-range" />
                  </div>

                  {/* On-air scheduling */}
                  <div className="pt-2 border-t border-zinc-800">
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] uppercase tracking-wide text-zinc-500">On-air scheduling</Label>
                      <Switch checked={!!schedOf(sel).on} onCheckedChange={(v) => patchSched({ on: v })} data-testid="gfx-sched-switch" />
                    </div>
                    {schedOf(sel).on ? (
                      <div className="mt-2 space-y-2" data-testid="gfx-sched-panel">
                        {(schedOf(sel).windows || []).map((w, i) => (
                          <div key={i} className="rounded-sm border border-zinc-800 p-2 space-y-1.5" data-testid={`gfx-sched-window-${i}`}>
                            <div className="flex items-center gap-1.5">
                              <input type="time" value={w.s || "18:00"} onChange={(e) => setWindow(i, { s: e.target.value })} className="h-7 rounded-sm bg-zinc-900 border border-zinc-700 text-xs px-1" data-testid={`gfx-sched-start-${i}`} />
                              <span className="text-zinc-500 text-xs">→</span>
                              <input type="time" value={w.e || "22:00"} onChange={(e) => setWindow(i, { e: e.target.value })} className="h-7 rounded-sm bg-zinc-900 border border-zinc-700 text-xs px-1" data-testid={`gfx-sched-end-${i}`} />
                              <button className="ml-auto text-red-400/70 hover:text-red-300" onClick={() => removeWindow(i)} data-testid={`gfx-sched-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                            </div>
                            <div className="flex gap-1">
                              {DOW.map(([lbl, dow], di) => {
                                const on = (w.days || []).includes(dow);
                                return <button key={di} onClick={() => toggleDay(i, dow)}
                                  className={`w-6 h-6 rounded-sm text-[10px] font-bold ${on ? "bg-cyan-500/80 text-black" : "bg-zinc-800 text-zinc-400"}`}
                                  data-testid={`gfx-sched-day-${i}-${dow}`}>{lbl}</button>;
                              })}
                              <span className="text-[9px] text-zinc-500 self-center ml-1">{(w.days || []).length ? "" : "every day"}</span>
                            </div>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" className="rounded-sm text-[11px] h-7" onClick={addWindow} data-testid="gfx-sched-add-window"><Plus className="w-3 h-3 mr-1" /> Add daily window</Button>
                        <div className="pt-1">
                          <Label className="text-[10px] uppercase tracking-wide text-zinc-500">One-off window (optional)</Label>
                          <div className="mt-1 flex items-center gap-1.5">
                            <input type="datetime-local" value={schedOf(sel).oneoff?.s || ""}
                              onChange={(e) => patchSched({ oneoff: { ...(schedOf(sel).oneoff || {}), s: e.target.value } })}
                              className="h-7 rounded-sm bg-zinc-900 border border-zinc-700 text-[11px] px-1 flex-1" data-testid="gfx-sched-oneoff-start" />
                            <input type="datetime-local" value={schedOf(sel).oneoff?.e || ""}
                              onChange={(e) => patchSched({ oneoff: { ...(schedOf(sel).oneoff || {}), e: e.target.value } })}
                              className="h-7 rounded-sm bg-zinc-900 border border-zinc-700 text-[11px] px-1 flex-1" data-testid="gfx-sched-oneoff-end" />
                            {schedOf(sel).oneoff && (
                              <button className="text-red-400/70 hover:text-red-300" onClick={() => patchSched({ oneoff: null })} data-testid="gfx-sched-oneoff-clear"><X className="w-3.5 h-3.5" /></button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-500 mt-1">Always on air. Enable to restrict to time windows.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Select a layer to edit.</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
